// =============================================================================
// Field Overlay Component - DocuSign-style draggable/resizable field
// =============================================================================

import React from 'react';
import type { SignatureTemplateField, SignatureFieldType } from '../../../../types/esign';

// =============================================================================
// Signer Colors (DocuSign-style: each signer gets a distinct color)
// =============================================================================

export const SIGNER_COLORS = [
  { border: '#2563eb', bg: 'rgba(37, 99, 235, 0.12)', handle: '#2563eb', label: 'Blue' },     // Signer 0
  { border: '#ea580c', bg: 'rgba(234, 88, 12, 0.12)', handle: '#ea580c', label: 'Orange' },    // Signer 1
  { border: '#16a34a', bg: 'rgba(22, 163, 74, 0.12)', handle: '#16a34a', label: 'Green' },     // Signer 2
  { border: '#9333ea', bg: 'rgba(147, 51, 234, 0.12)', handle: '#9333ea', label: 'Purple' },   // Signer 3
  { border: '#dc2626', bg: 'rgba(220, 38, 38, 0.12)', handle: '#dc2626', label: 'Red' },       // Signer 4
  { border: '#0891b2', bg: 'rgba(8, 145, 178, 0.12)', handle: '#0891b2', label: 'Teal' },      // Signer 5
];

export function getSignerColor(signerIndex: number) {
  return SIGNER_COLORS[signerIndex % SIGNER_COLORS.length];
}

// =============================================================================
// Field Type Icons (inline SVGs for performance)
// =============================================================================

const FIELD_ICONS: Record<SignatureFieldType, React.ReactNode> = {
  SIGNATURE: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 flex-shrink-0">
      <path d="M3.5 13.5l1.81-4.53a1 1 0 01.68-.55l8.1-1.8a1 1 0 01.98.26l1.42 1.42a1 1 0 01.26.98l-1.8 8.1a1 1 0 01-.55.68L9.87 20.5a1 1 0 01-1.17-.23L3.73 14.67a1 1 0 01-.23-1.17z" />
    </svg>
  ),
  INITIALS: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 flex-shrink-0">
      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
    </svg>
  ),
  DATE_SIGNED: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 flex-shrink-0">
      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
    </svg>
  ),
  TEXT: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 flex-shrink-0">
      <path fillRule="evenodd" d="M4 3a1 1 0 00-1 1v1a1 1 0 002 0h4v10H7a1 1 0 100 2h6a1 1 0 100-2h-2V5h4a1 1 0 100 0h1a1 1 0 001-1V4a1 1 0 00-1-1H4z" clipRule="evenodd" />
    </svg>
  ),
  CHECKBOX: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 flex-shrink-0">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  ),
  NAME: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 flex-shrink-0">
      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
    </svg>
  ),
  EMAIL_FIELD: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 flex-shrink-0">
      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
    </svg>
  ),
  COMPANY: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 flex-shrink-0">
      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
    </svg>
  ),
  TITLE_FIELD: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 flex-shrink-0">
      <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
    </svg>
  ),
};

const FIELD_TYPE_SHORT_LABELS: Record<SignatureFieldType, string> = {
  SIGNATURE: 'Signature',
  INITIALS: 'Initials',
  DATE_SIGNED: 'Date',
  TEXT: 'Text',
  CHECKBOX: 'Check',
  NAME: 'Name',
  EMAIL_FIELD: 'Email',
  COMPANY: 'Company',
  TITLE_FIELD: 'Title',
};

// =============================================================================
// Resize Handle Types
// =============================================================================

export type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w';

interface HandleDef {
  position: ResizeHandle;
  style: React.CSSProperties;
}

const RESIZE_HANDLES: HandleDef[] = [
  { position: 'nw', style: { top: -4, left: -4, cursor: 'nw-resize' } },
  { position: 'ne', style: { top: -4, right: -4, cursor: 'ne-resize' } },
  { position: 'sw', style: { bottom: -4, left: -4, cursor: 'sw-resize' } },
  { position: 'se', style: { bottom: -4, right: -4, cursor: 'se-resize' } },
  { position: 'n', style: { top: -4, left: '50%', marginLeft: -4, cursor: 'n-resize' } },
  { position: 's', style: { bottom: -4, left: '50%', marginLeft: -4, cursor: 's-resize' } },
  { position: 'w', style: { top: '50%', left: -4, marginTop: -4, cursor: 'w-resize' } },
  { position: 'e', style: { top: '50%', right: -4, marginTop: -4, cursor: 'e-resize' } },
];

// =============================================================================
// FieldOverlay Component
// =============================================================================

export interface FieldPosition {
  xPercent: number;
  yPercent: number;
  widthPercent: number;
  heightPercent: number;
}

interface FieldOverlayProps {
  field: SignatureTemplateField;
  position: FieldPosition;
  isSelected: boolean;
  isDragging: boolean;
  onSelect: (e: React.PointerEvent) => void;
  onDragStart: (e: React.PointerEvent) => void;
  onResizeStart: (e: React.PointerEvent, handle: ResizeHandle) => void;
}

export default function FieldOverlay({
  field,
  position,
  isSelected,
  isDragging,
  onSelect,
  onDragStart,
  onResizeStart,
}: FieldOverlayProps) {
  const color = getSignerColor(field.signerIndex);

  return (
    <div
      className="absolute transition-shadow"
      style={{
        left: `${position.xPercent}%`,
        top: `${position.yPercent}%`,
        width: `${position.widthPercent}%`,
        height: `${position.heightPercent}%`,
        minHeight: '20px',
        minWidth: '24px',
        zIndex: isDragging ? 50 : isSelected ? 10 : 1,
        border: `2px solid ${color.border}`,
        backgroundColor: color.bg,
        borderRadius: '3px',
        cursor: isDragging ? 'grabbing' : 'grab',
        boxShadow: isSelected
          ? `0 0 0 2px ${color.border}40, 0 2px 8px rgba(0,0,0,0.15)`
          : isDragging
            ? '0 4px 12px rgba(0,0,0,0.2)'
            : 'none',
      }}
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onSelect(e);
        onDragStart(e);
      }}
    >
      {/* Field label + icon */}
      <div
        className="flex items-center gap-1 px-1 h-full overflow-hidden"
        style={{ color: color.border }}
      >
        {FIELD_ICONS[field.fieldType]}
        <span className="text-[10px] font-semibold truncate leading-tight">
          {field.label || FIELD_TYPE_SHORT_LABELS[field.fieldType]}
        </span>
        {field.isRequired && (
          <span className="text-[8px] text-red-500 flex-shrink-0">*</span>
        )}
      </div>

      {/* Resize handles - only shown when selected */}
      {isSelected && !isDragging && (
        <>
          {RESIZE_HANDLES.map((h) => (
            <div
              key={h.position}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onResizeStart(e, h.position);
              }}
              style={{
                ...h.style,
                position: 'absolute',
                width: 8,
                height: 8,
                backgroundColor: color.handle,
                borderRadius: 1,
                border: '1px solid white',
              }}
            />
          ))}
        </>
      )}
    </div>
  );
}
